import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Pencil, Trash2, Save, X, Loader2, Eye, EyeOff, Globe, User, Lock, FileText, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../services/api';

// Credential Form Component (defined outside)
const CredentialForm = ({ form, setForm, onSave, onCancel, saving, isNew }) => (
  <div className="bg-slate-900/80 rounded-lg p-4 border border-indigo-500/30 space-y-3">
    <div className="flex items-center gap-2 mb-3">
      <Key className="w-4 h-4 text-indigo-400" />
      <span className="text-white font-medium text-sm">{isNew ? 'Add New Credential' : 'Edit Credential'}</span>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="text-xs text-slate-400 block mb-1">Credential Name *</label>
        <Input 
          placeholder="e.g., MCA V3 Portal, GST Portal"
          value={form.credential_name}
          onChange={(e) => setForm({...form, credential_name: e.target.value})}
          className="bg-slate-800 border-slate-700 text-white text-sm"
        />
      </div>
      <div>
        <label className="text-xs text-slate-400 block mb-1">URL</label>
        <Input 
          placeholder="https://example.com"
          value={form.url}
          onChange={(e) => setForm({...form, url: e.target.value})}
          className="bg-slate-800 border-slate-700 text-white text-sm"
        />
      </div>
      <div>
        <label className="text-xs text-slate-400 block mb-1">Username / User ID</label>
        <Input 
          placeholder="Enter username"
          value={form.username}
          onChange={(e) => setForm({...form, username: e.target.value})}
          className="bg-slate-800 border-slate-700 text-white text-sm"
        />
      </div>
      <div>
        <label className="text-xs text-slate-400 block mb-1">Password</label>
        <Input 
          type="password"
          placeholder="Enter password"
          value={form.password}
          onChange={(e) => setForm({...form, password: e.target.value})}
          className="bg-slate-800 border-slate-700 text-white text-sm"
        />
      </div>
    </div>
    
    <div>
      <label className="text-xs text-slate-400 block mb-1">Notes / Remarks</label>
      <textarea
        placeholder="Additional information, security questions, etc."
        value={form.notes}
        onChange={(e) => setForm({...form, notes: e.target.value})}
        rows={2}
        className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm p-2 resize-none"
      />
    </div>
    
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" size="sm" onClick={onCancel} className="border-slate-600 text-slate-300">
        <X className="w-4 h-4 mr-1" /> Cancel
      </Button>
      <Button size="sm" onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
        {isNew ? 'Add' : 'Save'}
      </Button>
    </div>
  </div>
);

// Credential Card Component (defined outside)
const CredentialCard = ({ cred, isEditing, showPassword, onEdit, onDelete, onTogglePassword, form, setForm, onUpdate, onCancel, saving }) => {
  if (isEditing) {
    return <CredentialForm form={form} setForm={setForm} onSave={onUpdate} onCancel={onCancel} saving={saving} isNew={false} />;
  }

  return (
    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <h4 className="text-white font-medium">{cred.credential_name}</h4>
            {cred.created_by_role && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                cred.created_by_role.includes('admin') ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
              }`}>
                {cred.created_by_role.includes('admin') ? 'Admin' : 'Client'}
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {cred.url && (
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-slate-500" />
                <a href={cred.url} target="_blank" rel="noopener noreferrer" 
                  className="text-indigo-400 hover:text-indigo-300 truncate flex items-center gap-1">
                  {cred.url.replace(/^https?:\/\//, '').substring(0, 30)}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
            {cred.username && (
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-slate-300 font-mono">{cred.username}</span>
              </div>
            )}
            {cred.password && (
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-slate-300 font-mono">
                  {showPassword ? cred.password : '••••••••'}
                </span>
                <button onClick={() => onTogglePassword(cred.id)} 
                  className="text-slate-500 hover:text-slate-300">
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
          </div>
          
          {cred.notes && (
            <div className="mt-2 flex items-start gap-2 text-sm">
              <FileText className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
              <span className="text-slate-400 italic">{cred.notes}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEdit(cred)}
            className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(cred.id)}
            className="p-2 rounded hover:bg-red-900/50 text-red-400 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const ExternalCredentials = ({ 
  entityType, // 'company' or 'director'
  entityId,
  entityName,
  isAdmin = false
}) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const emptyForm = {
    credential_name: '',
    url: '',
    username: '',
    password: '',
    notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  const loadCredentials = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const response = await api.get(`/credentials/${entityType}/${entityId}`);
      setCredentials(response.data || []);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const handleAdd = async () => {
    if (!form.credential_name) {
      toast.error('Credential name is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/credentials', {
        entity_type: entityType,
        entity_id: entityId,
        ...form
      });
      toast.success('Credential added successfully');
      setShowAddForm(false);
      setForm(emptyForm);
      loadCredentials();
    } catch (error) {
      toast.error('Failed to add credential');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cred) => {
    setEditingId(cred.id);
    setForm({
      credential_name: cred.credential_name || '',
      url: cred.url || '',
      username: cred.username || '',
      password: cred.password || '',
      notes: cred.notes || ''
    });
  };

  const handleUpdate = async () => {
    if (!form.credential_name) {
      toast.error('Credential name is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/credentials/${editingId}`, form);
      toast.success('Credential updated');
      setEditingId(null);
      setForm(emptyForm);
      loadCredentials();
    } catch (error) {
      toast.error('Failed to update credential');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (credId) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;
    
    try {
      await api.delete(`/credentials/${credId}`);
      toast.success('Credential deleted');
      loadCredentials();
    } catch (error) {
      toast.error('Failed to delete credential');
    }
  };

  const togglePasswordVisibility = (credId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [credId]: !prev[credId]
    }));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setForm(emptyForm);
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-400" />
            External Credentials
          </h3>
          <p className="text-sm text-slate-400 mt-0.5">
            {entityName ? `${entityName} - ` : ''}
            {credentials.length} credential{credentials.length !== 1 ? 's' : ''} stored
          </p>
        </div>
        
        {!showAddForm && !editingId && (
          <Button size="sm" onClick={() => setShowAddForm(true)} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-1" /> Add Credential
          </Button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="mb-4">
          <CredentialForm form={form} setForm={setForm} onSave={handleAdd} onCancel={cancelEdit} saving={saving} isNew={true} />
        </div>
      )}

      {/* Credentials List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
        </div>
      ) : credentials.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {credentials.map(cred => (
            <CredentialCard 
              key={cred.id} 
              cred={cred}
              isEditing={editingId === cred.id}
              showPassword={visiblePasswords[cred.id]}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTogglePassword={togglePasswordVisibility}
              form={form}
              setForm={setForm}
              onUpdate={handleUpdate}
              onCancel={cancelEdit}
              saving={saving}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-slate-700/50 rounded-lg">
          <Key className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500">No credentials stored yet</p>
          <p className="text-xs text-slate-600 mt-1">Click &quot;Add Credential&quot; to store login details securely</p>
        </div>
      )}
    </div>
  );
};

export default ExternalCredentials;
